<template>
    <ValidationObserver ref="formModalValidate">
        <b-form @submit.prevent="storeForm">
            <b-spinner v-if="dataLoading" small />
            <template v-if="formProcess !== 'update'">
                <ValidationProvider name="semester_id" rules="required" v-slot="{ errors }">
                    <b-form-group :label="$t('semester')">
                        <semesters-selectbox
                            v-model="formData.semester_id"
                            :multiple="false"
                            :validate-error="errors[0]"
                        />
                    </b-form-group>
                </ValidationProvider>
                <ValidationProvider name="program_code" rules="required" v-slot="{ errors }">
                    <b-form-group :label="$t('program')">
                        <div class="d-flex align-items-start">
                            <program-selectbox
                                class="flex-grow-1"
                                style="min-width: 0"
                                v-model="formData.program_code"
                                :multiple="false"
                                :validate-error="errors[0]"
                            />
                            <b-button
                                v-if="formProcess === 'create'"
                                type="button"
                                variant="primary"
                                class="ml-2 flex-shrink-0"
                                :disabled="!formData.program_code || studentsLoading"
                                @click="searchStudents"
                            >
                                <b-spinner v-if="studentsLoading" small />
                                {{ $t('search') }}
                            </b-button>
                        </div>
                    </b-form-group>
                </ValidationProvider>
                <ValidationProvider
                    v-if="formProcess === 'create'"
                    name="student_program_id"
                    rules="required"
                    v-slot="{ errors }"
                >
                    <b-form-group :label="$t('student')">
                        <multi-selectbox
                            v-model="formData.student_program_id"
                            :options="studentOptions"
                            :multiple="false"
                            :disabled="studentsLoading"
                            :validate-error="errors[0]"
                        />
                    </b-form-group>
                </ValidationProvider>
                <ValidationProvider v-if="formProcess === 'bulk'" name="student_numbers" rules="required" v-slot="{ errors }">
                    <b-form-group :label="$t('student_numbers')">
                        <lined-textarea
                            v-model="formData.student_numbers"
                            :nowrap="false"
                            :disabled="false"
                            :styles="{ height: '10em', resize: 'both' }"
                            :validateError="errors[0]"
                        />
                        <div class="invalid-feedback d-block" v-if="errors[0]">{{ errors[0] }}</div>
                    </b-form-group>
                </ValidationProvider>
            </template>
            <p v-else>{{ selectedStudent }}</p>
            <ValidationProvider
                name="vi_application_submitted"
                rules="required"
                v-slot="{ errors }"
            >
                <b-form-group :label="$t('vi_application_submitted')">
                    <yes-no-selectbox
                        v-model="formData.vi_application_submitted"
                        :validate-error="errors[0]"
                    />
                </b-form-group>
            </ValidationProvider>
            <div class="d-flex justify-content-end">
                <b-button
                    variant="outline-secondary"
                    class="mr-2"
                    :disabled="formLoading"
                    @click="$emit('cancel')"
                >
                    {{ $t('cancel') }}
                </b-button>
                <b-button type="submit" variant="primary" :disabled="formLoading || dataLoading">
                    <b-spinner v-if="formLoading" small />
                    {{ $t('save') }}
                </b-button>
            </div>
        </b-form>
    </ValidationObserver>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import VirtualInternshipsProgramService from '@/services/VirtualInternshipsProgramService';
import LinedTextarea from '@/components/elements/LinedTextarea.vue';
import MultiSelectbox from '@/components/interactive-fields/MultiSelectbox';
import YesNoSelectbox from '@/components/interactive-fields/YesNoSelectbox';
import StudentProgramService from '@/services/StudentProgramService';
import qs from 'qs';

export default {
    name: 'VirtualInternshipsProgramForm',
    components: {
        ValidationObserver,
        ValidationProvider,
        LinedTextarea,
        MultiSelectbox,
        YesNoSelectbox
    },
    props: {
        formId: { type: Number, default: null },
        formProcess: { type: String, default: 'create' },
        semesterId: { type: [Number, String], default: null },
        selectedStudent: { type: String, default: '' }
    },
    data() {
        return {
            formData: {
                semester_id: this.semesterId,
                student_program_id: null,
                program_code: null,
                student_numbers: '',
                vi_application_submitted: this.formProcess === 'update' ? null : 'No'
            },
            formLoading: false,
            dataLoading: false,
            studentsLoading: false,
            studentOptions: []
        };
    },
    watch: {
        'formData.program_code'() {
            this.formData.student_program_id = null;
            this.studentOptions = [];
        }
    },
    created() {
        if (this.formProcess === 'update') this.get(this.formId);
    },
    methods: {
        searchStudents() {
            this.studentsLoading = true;
            this.formData.student_program_id = null;
            this.studentOptions = [];

            const config = {
                params: {
                    filter: {
                        program_code: this.formData.program_code
                    },
                    sort: '-id',
                    limit: -1
                },
                paramsSerializer: (params) => qs.stringify(params, {encode: false})
            };

            return StudentProgramService.getAll(config)
                .then((response) => {
                    this.studentOptions = response.data.data.map((student) => ({
                        value: student.id,
                        text: `${student.student_number} - ${student.name} ${student.surname}`
                    }));
                })
                .catch((error) => {
                    this.showErrors(error, this.$refs.formModalValidate);
                })
                .finally(() => {
                    this.studentsLoading = false;
                });
        },
        get(id) {
            this.dataLoading = true;
            return VirtualInternshipsProgramService.get(id)
                .then((response) => {
                    this.formData.vi_application_submitted = response.data.data.vi_application_submitted;
                })
                .catch((error) => {
                    this.showErrors(error, this.$refs.formModalValidate);
                })
                .finally(() => {
                    this.dataLoading = false;
                });
        },
        async storeForm() {
            const isValid = await this.$refs.formModalValidate.validate();
            if (isValid) {
                this.formLoading = true;
                this.$emit('formLoading', true);
                const status = { vi_application_submitted: this.formData.vi_application_submitted };
                let request;

                if (this.formProcess === 'update') {
                    request = VirtualInternshipsProgramService.update(this.formId, status);
                } else if (this.formProcess === 'bulk') {
                    request = VirtualInternshipsProgramService.bulkStore({
                        ...status,
                        semester_id: this.formData.semester_id,
                        program_code: this.formData.program_code,
                        student_numbers: this.formData.student_numbers.split(/\r?\n/)
                    });
                } else {
                    request = VirtualInternshipsProgramService.store({
                        semester_id: this.formData.semester_id,
                        student_program_id: this.formData.student_program_id,
                        ...status
                    });
                }

                return request
                    .then(() => {
                        this.$toast.success(this.$t('vi_saved'));
                        this.$emit(
                            this.formProcess === 'update' ? 'updateFormSuccess' : 'createFormSuccess'
                        );
                    })
                    .catch((error) => {
                        this.showErrors(error, this.$refs.formModalValidate);
                    })
                    .finally(() => {
                        this.formLoading = false;
                        this.$emit('formLoading', false);
                    });
            }
        }
    }
};
</script>
